/**
 * One CUEC card (Section 3). Reworked 2026-05-29 for #73/#74/#75 + GAP8:
 *  - Header (#74): CUEC# · short title · control objective (category) · coverage
 *    · status (Needs Review / Completed). Status is independent of coverage —
 *    a partial-coverage CUEC with its A19 narrative filled reads Completed.
 *  - Editable "Management's response" field (#122) — seeded from the AIP-6 draft,
 *    persists to compensating_narrative. Partial/none/na can't be Done until it
 *    has text; comments below are an optional discussion thread.
 *  - A15 (N/A rationale) vs A19 (coverage-gap doc) split (GAP8).
 *  - Mapped RCM controls are clickable → read-only detail modal (#75); the
 *    Multiselect keeps its X-to-remove.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  ExpandableSection,
  FormField,
  Input,
  Modal,
  Multiselect,
  Select,
  SpaceBetween,
  StatusIndicator,
  Textarea,
} from "@cloudscape-design/components";
import type { Cuec, RcmControl } from "@/core/api/types";
import { CommentThread } from "@/core/ui/CommentThread";
import { cuecVerdict } from "@/core/ui/decisionLabel";
import { PdfPageLink } from "@/core/ui/PdfPageLink";
import { RcmDetailModal } from "@/core/ui/RcmDetailModal";
import { SubcardHeader } from "@/core/ui/SubcardHeader";
import { formatET } from "@/core/ui/time";
import { useSavedFlash } from "@/core/ui/useSavedFlash";
import { deleteCuec, updateCuec, updateCuecMappings } from "./api";

type Coverage = "full" | "partial" | "none" | "na";

const COVERAGE_OPTIONS = [
  { label: "Full — RCM fully covers", value: "full" as const },
  { label: "Partial — gaps remain", value: "partial" as const },
  { label: "None — no RCM coverage", value: "none" as const },
  { label: "N/A — not applicable", value: "na" as const },
];

export function CuecCard({
  cuec,
  index,
  rcmControls,
  reportId,
  onUpdated,
  focusTargetId,
  pageOverride,
}: {
  cuec: Cuec;
  index: number;
  rcmControls: RcmControl[];
  reportId: string;
  onUpdated: () => void;
  focusTargetId?: string | null;
  /** #100: the first CUEC's page — used for every CUEC's link (pages don't drift). */
  pageOverride?: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isFocused = focusTargetId === cuec.id;
  useEffect(() => {
    if (isFocused && containerRef.current) {
      setExpanded(true);
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isFocused]);

  const ai = cuec.ai_data;
  const assessment = ai?.assessment;
  // #96: a legacy row flagged applicable=false maps to the new 'na' coverage.
  const [coverage, setCoverage] = useState<Coverage>(
    cuec.applicable === false ? "na" : ((cuec.coverage as Coverage | null) ?? "none")
  );
  const [shortTitle, setShortTitle] = useState(cuec.short_title ?? "");
  // #122: management's response is an editable field (open → editable, Done → Edit
  // to reopen), seeded from management's saved override, else the AIP-6 draft, and
  // persisted to compensating_narrative — like the control-exception card.
  const aiAssessment = assessment?.compensating_assessment || assessment?.rationale || "";
  const [mgmtResponse, setMgmtResponse] = useState(cuec.compensating_narrative ?? aiAssessment);
  const [marked, setMarked] = useState(!!cuec.marked_done);
  const [mappingIds, setMappingIds] = useState<string[]>(cuec.mappings.map((m) => m.id));
  const [busy, setBusy] = useState(false);
  const [savedAt, flashSaved] = useSavedFlash(); // #119: auto-dismisses after ~5s
  const [error, setError] = useState<string | null>(null);
  const [detailControl, setDetailControl] = useState<RcmControl | null>(null);
  const [expanded, setExpanded] = useState(isFocused);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // #99: a Done CUEC opens read-only; the fields unlock only after clicking Edit.
  const [editing, setEditing] = useState(false);
  // #C8: leave edit mode on a Done CUEC without saving — restore displayed values.
  const cancelEdit = () => {
    setCoverage(cuec.applicable === false ? "na" : ((cuec.coverage as Coverage | null) ?? "none"));
    setShortTitle(cuec.short_title ?? "");
    setMappingIds(cuec.mappings.map((m) => m.id));
    setMgmtResponse(cuec.compensating_narrative ?? aiAssessment);
    setEditing(false);
  };

  const removeCuec = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteCuec(cuec.id);
      setConfirmDelete(false);
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const multiOptions = useMemo(
    () => rcmControls.map((r) => ({ label: `${r.code} — ${r.name}`, value: r.id })),
    [rcmControls]
  );
  const selectedMappings = useMemo(
    () => multiOptions.filter((o) => mappingIds.includes(o.value)),
    [multiOptions, mappingIds]
  );
  const mappedControls = useMemo(
    () => mappingIds.map((id) => rcmControls.find((r) => r.id === id)).filter((r): r is RcmControl => !!r),
    [mappingIds, rcmControls]
  );

  // #96: the Coverage dropdown is the single control — 'na' = not applicable
  // (no separate Applicable toggle). 'na' resolves on its own; everything else
  // is Done only via an explicit Mark done. Changing coverage never auto-flips,
  // and partial/none can't be Done until a human documents the compensating
  // controls in a (non-AI) comment.
  const applicable = coverage !== "na";
  // #122: partial/none AND N/A all need a documented rationale in the Management's
  // response field before Mark done — compensating controls for partial/none, the
  // not-applicable reason for na. Comments are now optional. Only full coverage can
  // be Done without a response.
  const needsResponse = coverage === "partial" || coverage === "none" || coverage === "na";
  const responseFilled = mgmtResponse.trim().length > 0;
  const completed = marked;
  const needsValidation = needsResponse && !marked && !responseFilled;
  const canMarkDone = coverage === "full" || (needsResponse && responseFilled);
  // #99: read-only once Done — fields are locked until the user clicks Edit. An
  // open CUEC is directly editable.
  const readOnly = completed && !editing;
  const inputsDisabled = busy || readOnly;

  // #96: Save persists edits and leaves the CUEC OPEN — only Mark done finalizes.
  const persist = async (mark: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await updateCuec(cuec.id, {
        applicable,
        coverage,
        short_title: shortTitle.trim() || null,
        compensating_narrative: mgmtResponse.trim() || null,
        mark_done: mark,
      });
      await updateCuecMappings(cuec.id, { rcm_control_ids: mappingIds });
      setMarked(mark);
      setEditing(false);
      flashSaved();
      if (mark) setExpanded(false); // collapse once Done
      onUpdated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  const save = () => persist(false); // Save = persist + stay Open
  const markDone = () => persist(true);

  const title = shortTitle.trim() || cuec.text.slice(0, 80) + (cuec.text.length > 80 ? "…" : "");

  return (
    <div ref={containerRef} className={isFocused ? "focus-flash" : undefined}>
      <ExpandableSection
        variant="container"
        expanded={expanded}
        onChange={({ detail }) => setExpanded(detail.expanded)}
        header={
          <SubcardHeader
            title={`CUEC ${index} · ${title}`}
            status={completed ? <Badge color="green">Done</Badge> : <Badge color="red">Open</Badge>}
            actions={
              <>
                {ai?.category && <Badge color="grey">{ai.category}</Badge>}
                {/* #114: the coverage verdict shows only once the CUEC is Done. */}
                {completed && <Badge color="blue">{cuecVerdict(coverage)}</Badge>}
                {(pageOverride ?? ai?.page) != null && (
                  <PdfPageLink reportId={reportId} page={(pageOverride ?? ai?.page)!} />
                )}
                <Button
                  iconName="remove"
                  variant="inline-link"
                  ariaLabel={`Delete CUEC ${index}`}
                  onClick={() => setConfirmDelete(true)}
                />
              </>
            }
          />
        }
      >
        <SpaceBetween size="s">
          {error && (
            <Alert type="error" header="Save failed">
              {error}
            </Alert>
          )}

          {needsValidation &&
            (coverage === "na" ? (
              <Alert type="info" header="Not applicable requires a documented rationale">
                Explain why this CUEC is not applicable to your organization in the Management's response
                field below — <b>Mark done</b> stays disabled until it has text.
              </Alert>
            ) : (
              <Alert type="warning" header="Partial coverage requires additional management validation">
                The RCM controls don't fully cover this CUEC. Document the compensating controls or
                additional procedures in the Management's response field below — <b>Mark done</b> stays
                disabled until it has text. The AI never marks a partial/none CUEC done.
              </Alert>
            ))}

          <FormField label="Title">
            <Input
              value={shortTitle}
              onChange={({ detail }) => setShortTitle(detail.value)}
              placeholder="Short, descriptive title"
              disabled={inputsDisabled}
            />
          </FormField>

          <Box>
            <Box variant="awsui-key-label">CUEC text</Box>
            <Box>{cuec.text}</Box>
          </Box>

          <Box>
            <Box variant="awsui-key-label">Coverage</Box>
            <Select
              selectedOption={COVERAGE_OPTIONS.find((o) => o.value === coverage) ?? COVERAGE_OPTIONS[2]!}
              onChange={({ detail }) => {
                const next = detail.selectedOption.value as Coverage;
                setCoverage(next);
                // #96/#117: any non-full coverage needs a fresh rationale + an
                // explicit Mark done — it can't stay Done on a coverage change.
                if (next !== "full") setMarked(false);
              }}
              options={COVERAGE_OPTIONS}
              disabled={inputsDisabled}
            />
          </Box>

          {applicable && (
            <Box>
              <Box variant="awsui-key-label">RCM mapping</Box>
              <Multiselect
                selectedOptions={selectedMappings}
                onChange={({ detail }) => setMappingIds(detail.selectedOptions.map((o) => o.value as string))}
                options={multiOptions}
                placeholder="Map to RCM controls"
                virtualScroll
                filteringType="auto"
                disabled={inputsDisabled}
              />
              {mappedControls.length > 0 && (
                <Box padding={{ top: "xs" }}>
                  <Box variant="small" color="text-body-secondary">
                    Click a mapped control to view its detail:
                  </Box>
                  <SpaceBetween direction="horizontal" size="xs">
                    {mappedControls.map((c) => (
                      <Button key={c.id} variant="inline-link" onClick={() => setDetailControl(c)}>
                        {c.code}
                      </Button>
                    ))}
                  </SpaceBetween>
                </Box>
              )}
            </Box>
          )}

          <FormField
            label="Management's response"
            description="The AI's assessment of compensating controls (or the N/A rationale) — edit to make it management's own."
          >
            <Textarea
              value={mgmtResponse}
              onChange={({ detail }) => setMgmtResponse(detail.value)}
              rows={4}
              disabled={inputsDisabled}
              placeholder="How the user org's controls compensate, or why this CUEC is not applicable."
            />
          </FormField>

          <Box>
            <Box variant="awsui-key-label">Comments</Box>
            <CommentThread kind="cuec" targetId={cuec.id} onChange={onUpdated} />
          </Box>

          <SpaceBetween direction="horizontal" size="xs">
            {readOnly ? (
              // #99: a Done CUEC is read-only — Edit unlocks the fields.
              <Button iconName="edit" onClick={() => setEditing(true)}>
                Edit
              </Button>
            ) : (
              <>
                <Button variant="primary" onClick={save} disabled={busy} loading={busy}>
                  Save
                </Button>
                <Button iconName="check" onClick={markDone} disabled={busy || !canMarkDone}>
                  Mark done
                </Button>
                {/* #C8: Edit on a Done CUEC can be backed out. */}
                {editing && (
                  <Button onClick={cancelEdit} disabled={busy}>
                    Cancel
                  </Button>
                )}
              </>
            )}
            {/* #96/#117: explain why Mark done is unavailable (Button has no tooltip). */}
            {!readOnly && !canMarkDone && (
              <Box variant="small" color="text-status-inactive" padding={{ top: "xxs" }}>
                {coverage === "na"
                  ? "Add a rationale in the Management's response field to mark this CUEC N/A."
                  : "Document the compensating controls in the Management's response field to mark done."}
              </Box>
            )}
            {savedAt && !busy && (
              <StatusIndicator type="success">
                {completed ? "Done" : "Saved"} {formatET(new Date(savedAt))}
              </StatusIndicator>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </ExpandableSection>

      <RcmDetailModal control={detailControl} onDismiss={() => setDetailControl(null)} />

      <Modal
        visible={confirmDelete}
        onDismiss={() => setConfirmDelete(false)}
        header="Delete CUEC"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setConfirmDelete(false)} disabled={busy}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void removeCuec()} disabled={busy} loading={busy}>
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        Delete this CUEC and its RCM mappings? This can't be undone.
      </Modal>
    </div>
  );
}
